import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { ComponentIds } from '@inter-ikea-core/core';
import { OpeningFunction, ProductType } from '@inter-ikea-storageone/range';
import { reportSelectOpeningFunction } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { getFilterState, getMarketProducts, getSelectedGroupId } from 'ducks/products';
import { hideMixedOpeningTypeMainMenu } from 'rangeConfig/shared/rules/hideMenuRules';
import {
  changeAllOpeningFunctionInScene,
  getFrontOpeningFunctionsInScene
} from 'rangeConfig/shared/utils/frontLayouts';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Box from 'ui/Box';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import { RootState } from 'ducks';
import { useBehaviorTracker } from 'contexts/behaviors';
import SegmentedControl from '@ingka/segmented-control';

const openingFunctionMap = {
  [ProductType.BESTA_PUSH_OPEN as string]: 'push_to_open',
  [ProductType.BESTA_SOFT_CLOSING as string]: 'soft_closing'
};

export default function FrontOpeningFunctionToggleMainMenuMobile() {
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const behaviorTracker = useBehaviorTracker();
  const currentOpeningFunctions = getFrontOpeningFunctionsInScene(coreApi);

  const { products } = useSelector((state: RootState) => ({
    products: getMarketProducts(state),
    filterState: getFilterState(state),
    selectedGroupId: getSelectedGroupId(state)
  }));

  const handleSelect = (openingFunction: OpeningFunction) => {
    changeAllOpeningFunctionInScene(openingFunction, products, coreApi);
    if (openingFunction === ProductType.BESTA_PUSH_OPEN) {
      const handles = coreApi.entities.getByComponent(ComponentIds.HANDLE_COMPONENT_ID);
      coreApi.entities.deleteMultiple(handles);
    }

    reportSelectOpeningFunction({ tab: openingFunctionMap[openingFunction] });
  };

  const openingTypeOptions = [
    {
      id: 'push-open',
      title: 'storageone.productPushToOpen',
      caption: 'storageone.productListNoHandlesOptionTitle',
      openingFunction: ProductType.BESTA_PUSH_OPEN,
      onSelect: () => handleSelect(ProductType.BESTA_PUSH_OPEN)
    },
    {
      id: 'soft-closing',
      title: 'storageone.productSoftClosing',
      caption: 'storageone.productListSoftClosingWithHandlesSubtitle',
      openingFunction: ProductType.BESTA_SOFT_CLOSING,
      onSelect: () => handleSelect(ProductType.BESTA_SOFT_CLOSING)
    }
  ];

  const items = openingTypeOptions.map(option => ({
    id: option.id,
    label: formatMessage({ id: option.title }),
    icon: ''
  }));

  const pushOpen = currentOpeningFunctions.every(
    openingFunction => openingFunction === ProductType.BESTA_PUSH_OPEN
  );
  const softClosing = currentOpeningFunctions.every(
    openingFunction => openingFunction === ProductType.BESTA_SOFT_CLOSING
  );
  const selectedIndex = pushOpen
    ? openingTypeOptions.findIndex(option => option.openingFunction === ProductType.BESTA_PUSH_OPEN)
    : softClosing
    ? openingTypeOptions.findIndex(
        option => option.openingFunction === ProductType.BESTA_SOFT_CLOSING
      )
    : undefined;

  const onToggleSelect = (index: number) =>
    behaviorTracker.custom(() => openingTypeOptions[index].onSelect(), true);

  return (
    <Box
      mt={hideMixedOpeningTypeMainMenu(coreApi) ? 'l' : 0}
      mb="0"
      mx="l"
      style={{ width: '100%' }}
    >
      {hideMixedOpeningTypeMainMenu(coreApi) ? (
        <></>
      ) : (
        <Box mb="md">
          <SkapaInlineMessage
            body={formatMessage({ id: 'storageone.productListMixedOpeningTypeMessage' })}
            variant={InlineMessageVariantEnum.INFORMATIVE}
            ssrIcon={InformationCircle}
            subtle
          />
        </Box>
      )}
      <SegmentedControl
        data-testid="handle-front-type-toggle"
        items={items}
        onClick={(e, value) => onToggleSelect(value)}
        activeIndex={selectedIndex}
      />
    </Box>
  );
}
